import { useEffect, useState, useMemo } from 'react';
import Image from 'next/image';

import styles from './InternalBlogPage.module.scss';

// SNS URLS
export const TWITTER_URL = 'https://twitter.com/intent/tweet';
export const FACEBOOK_URL = 'https://facebook.com/sharer';

export const generateTwitterIntentUrl = ({
  currentUrl,
  title,
}: {
  currentUrl: string;
  hashtags?: Array<string>;
  title: string;
}) => {
  const intentSearchParams = new URLSearchParams();
  intentSearchParams.append('url', encodeURI(currentUrl));
  // twitter appends the url to the tweet. Tweet characters are scarce so remove this
  // url from the text if it exists
  const textWithoutUrl = title.replace(currentUrl, '');
  intentSearchParams.append('text', textWithoutUrl);
  return `${TWITTER_URL}?${intentSearchParams.toString()}`;
};

export const generateFacebookSharerUrl = ({
  currentUrl,
  title,
}: {
  currentUrl: string;
  title: string;
}) => {
  const intentSearchParams = new URLSearchParams();
  intentSearchParams.append('u', currentUrl);
  intentSearchParams.append('quote', title);
  return `${FACEBOOK_URL}?${intentSearchParams.toString()}`;
};

// eslint-disable-next-line no-magic-numbers
const [SMALL_ICON, MEDIUM_ICON] = [32, 44];
const SNSInternalBlogShareRow = ({
  title,
  textAlign = 'left',
  iconSize,
}: {
  iconSize: 'small' | 'medium';
  textAlign?: 'left' | 'center' | 'right';
  title: string;
}) => {
  let iconSizeInPixels: number;
  switch (iconSize) {
    case 'small': {
      iconSizeInPixels = SMALL_ICON;
      break;
    }
    case 'medium':
    default: {
      iconSizeInPixels = MEDIUM_ICON;
    }
  }
  const [fullUrl, setFullUrl] = useState('');
  useEffect(() => {
    setFullUrl(window.location.href);
  }, []);
  const { facebookUrl, twitterUrl } = useMemo(() => {
    if (fullUrl.length > 0 && title) {
      const fullUrlAsURL = new URL(fullUrl);
      const { href } = fullUrlAsURL;
      return {
        facebookUrl: generateFacebookSharerUrl({ currentUrl: href, title }),
        twitterUrl: generateTwitterIntentUrl({ currentUrl: href, title }),
      };
    }

    return { facebookUrl: '', twitterUrl: '' };
  }, [fullUrl, title]);
  return (
    <div className={styles['sns-container']} style={{ textAlign }}>
      <a href={facebookUrl} target="_blank" rel="noreferrer" aria-label="Our Facebook">
        <Image
          className={styles.sns}
          alt="Facebook"
          height={iconSizeInPixels}
          src="/static/images/social/facebook_icon.png"
          unoptimized={true}
          width={iconSizeInPixels}
        />
      </a>
      <a href={twitterUrl} aria-label="Our Twitter" target="_blank" rel="noreferrer">
        <Image
          className={styles.sns}
          alt="Twitter"
          height={iconSizeInPixels}
          src="/static/images/social/twitter_icon.svg"
          unoptimized={true}
          width={iconSizeInPixels}
        />
      </a>
    </div>
  );
};

export default SNSInternalBlogShareRow;
